// UserDetails/DocumentSection.jsx

import React, { useState } from "react";

// Helper to check for a valid image URL
const isValidImageUrl = (url) => {
  return url && (url.startsWith("http") || url.startsWith("/"));
};

export default function DocumentSection({ docs, onResubmit }) {
  const [popupImage, setPopupImage] = useState(null);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [resubmitError, setResubmitError] = useState(null);

  const openPopup = (url) => setPopupImage(url);
  const closePopup = () => setPopupImage(null);

  const handleResubmit = async () => {
    try {
      setIsResubmitting(true);
      setResubmitError(null);
      await onResubmit();
      // Show success message or handle as needed
    } catch (error) {
      setResubmitError(error.message || 'Failed to request resubmission');
    } finally {
      setIsResubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-5 flex flex-col">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Documents</h2>
      
      {/* Error Message */}
      {resubmitError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="text-red-600 text-sm">{resubmitError}</div>
          <button
            onClick={() => setResubmitError(null)}
            className="text-red-500 hover:text-red-700 text-xs mt-1"
          >
            ✕ Dismiss
          </button>
        </div>
      )}

      <div className="flex-grow space-y-4">
        {!docs || Object.keys(docs).length === 0 ? (
          <div className="text-sm text-gray-400">
            No document information available.
          </div>
        ) : (
          <>
            <div>
              <label className="font-semibold text-gray-600 text-sm">
                Aadhaar Number:
              </label>
              <p className="text-gray-800 text-sm">
                {docs.adhaar || "Not Available"}
              </p>
              <div className="mt-2 border border-gray-200 rounded-md p-2 bg-white flex items-center justify-center min-h-[120px] shadow-sm">
                {isValidImageUrl(docs.adhaarImageUrl) ? (
                  <img
                    src={docs.adhaarImageUrl}
                    alt="Aadhaar"
                    className="max-h-32 w-auto rounded cursor-pointer hover:opacity-80 transition-opacity p-1 border border-gray-100 bg-white"
                    onClick={() => openPopup(docs.adhaarImageUrl)}
                  />
                ) : (
                  <div className="text-gray-400 text-xs text-center">
                    No image uploaded
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="font-semibold text-gray-600 text-sm">
                Pan Number:
              </label>
              <p className="text-gray-800 text-sm">
                {docs.pan || "Not Available"}
              </p>
              <div className="mt-2 border border-gray-200 rounded-md p-2 bg-white flex items-center justify-center min-h-[120px] shadow-sm">
                {isValidImageUrl(docs.panImageUrl) ? (
                  <img
                    src={docs.panImageUrl}
                    alt="PAN"
                    className="max-h-32 w-auto rounded cursor-pointer hover:opacity-80 transition-opacity p-1 border border-gray-100 bg-white"
                    onClick={() => openPopup(docs.panImageUrl)}
                  />
                ) : (
                  <div className="text-gray-400 text-xs text-center">
                    No image uploaded
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      
      <button
        className={`mt-4 w-full px-4 py-2 rounded-md font-semibold text-sm transition-colors ${
          isResubmitting
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-red-600 hover:bg-red-700 text-white'
        }`}
        onClick={handleResubmit}
        disabled={isResubmitting}
      >
        {isResubmitting ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
            <span>Processing...</span>
          </div>
        ) : (
          'Request for re-submission'
        )}
      </button>

      {popupImage && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={closePopup}
        >
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh] overflow-hidden">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 z-10 bg-gray-800 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-700 transition-colors"
            >
              ✕
            </button>
            <div className="p-4">
              <img
                src={popupImage}
                alt="Document Preview"
                className="max-h-[80vh] max-w-full object-contain border border-gray-200 rounded-md p-1 bg-white"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
